
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Paper,
  Button,
  CircularProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { styled } from '@mui/material/styles';
import { Filter as FilterIcon, Download as DownloadIcon, BarChart3 as BarChartLogoIcon } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  height: '100%'
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  height: 400,
  width: '100%',
  marginTop: theme.spacing(3)
}));

const CloudTalkKPI = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  
  // Filters
  const [period, setPeriod] = useState('this_week');
  const [metric, setMetric] = useState('calls_count');
  const [groupBy, setGroupBy] = useState('day');
  
  const [filters, setFilters] = useState({
    agent_id: '',
    direction: '',
    status: ''
  });

  const fetchAgents = async () => {
    try {
      const response = await api.get('/cloudtalk/agents');
      setAgentOptions(response.data?.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const agentFilter = filters.agent_id || '';
      const agent_id = agentFilter.startsWith('id:') ? agentFilter.slice(3) : '';
      const agent_name = agentFilter.startsWith('name:') ? agentFilter.slice(5) : '';

      const response = await api.get('/cloudtalk/kpi', {
        params: {
          period,
          metric,
          group_by: groupBy,
          ...filters,
          agent_id: agent_id || undefined,
          agent_name: agent_name || undefined
        }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching KPI data:', error);
      toast.error('Errore nel caricamento dei dati KPI');
    } finally {
      setLoading(false);
    }
  };

  const agentSelectOptions = useMemo(() => {
    const map = new Map();
    agentOptions.forEach((a) => {
      const rawName = a.agent_name || a.name || '(unknown)';
      const name = (rawName || '').trim() || '(unknown)';
      const key = name.toLowerCase();
      if (!map.has(key)) {
        const id = a.agent_id;
        map.set(key, {
          label: name,
          value: id ? `id:${id}` : `name:${name}`
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'it', { sensitivity: 'base' })
    );
  }, [agentOptions]);
  const selectedAgent = agentSelectOptions.find((o) => o.value === filters.agent_id) || null;

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    fetchData();
  }, [period, metric, groupBy, filters]);

  const getChartTitle = () => {
    const metricLabels = {
      'calls_count': 'Numero Chiamate',
      'avg_duration': 'Durata Media (sec)',
      'total_duration': 'Durata Totale (min)',
      'missed_rate': 'Tasso Chiamate Perse (%)',
      'answered_rate': 'Tasso Risposta (%)',
      'wait_time': 'Tempo Medio Attesa (sec)'
    };
    return metricLabels[metric] || metric;
  };

  const formatDateTick = (value) => {
    if (!value) return '';
    const str = String(value);
    const parts = str.split('-');

    if (groupBy === 'week' && parts.length === 2) {
      const [y, w] = parts;
      return `Sett. ${w}/${y}`;
    }

    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${d}/${m}/${y}`;
    }

    if (parts.length === 2) {
      const [y, m] = parts;
      return `01/${m}/${y}`;
    }

    return str;
  };

  const buildWeekData = (rawData) => {
    if (!rawData || rawData.length === 0) return rawData;

    const today = new Date();
    const day = today.getDay() || 7; // 1..7 (Lun=1, Dom=7)
    const monday = new Date(today);
    monday.setDate(today.getDate() - (day - 1));
    monday.setHours(0, 0, 0, 0);

    const map = new Map();
    rawData.forEach((row) => {
      if (!row.date) return;
      const key = String(row.date).slice(0, 10);
      map.set(key, row);
    });

    const result = [];
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const dayStr = String(d.getDate()).padStart(2, '0');
      const iso = `${year}-${month}-${dayStr}`;
      const existing = map.get(iso);
      if (existing) {
        result.push(existing);
      } else if (metric === 'calls_count' || !metric) {
        result.push({ date: iso, total: 0, inbound: 0, outbound: 0, missed: 0 });
      } else {
        result.push({ date: iso, value: 0 });
      }
    }

    return result;
  };

  const buildCurrentMonthData = (rawData) => {
    if (!rawData || rawData.length === 0) return rawData;

    const today = new Date();
    const year = today.getFullYear();
    const monthIndex = today.getMonth(); // 0-based

    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);

    const map = new Map();
    rawData.forEach((row) => {
      if (!row.date) return;
      const key = String(row.date).slice(0, 10);
      map.set(key, row);
    });

    const result = [];
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dayStr = String(d.getDate()).padStart(2, '0');
      const iso = `${y}-${m}-${dayStr}`;
      const existing = map.get(iso);
      if (existing) {
        result.push(existing);
      } else if (metric === 'calls_count' || !metric) {
        result.push({ date: iso, total: 0, inbound: 0, outbound: 0, missed: 0 });
      } else {
        result.push({ date: iso, value: 0 });
      }
    }

    return result;
  };

  const renderChart = () => {
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}><CircularProgress /></Box>;
    if (!data || data.length === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>Nessun dato disponibile per i filtri selezionati</Box>;

    let chartData = data;
    if (groupBy === 'day' && period === 'this_week') {
      chartData = buildWeekData(data);
    } else if (groupBy === 'day' && period === 'this_month') {
      chartData = buildCurrentMonthData(data);
    }

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    };

    if (metric === 'calls_count') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickFormatter={formatDateTick}
            />
            <YAxis tick={{fontSize: 12}} />
            <Tooltip 
              labelFormatter={formatDateTick}
              contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              cursor={{ fill: 'rgba(0,0,0,0.05)' }}
            />
            <Legend wrapperStyle={{ paddingTop: 20 }} />
            <Bar dataKey="inbound" name="Entrata" fill="#10b981" stackId="a" radius={[0, 0, 4, 4]} />
            <Bar dataKey="outbound" name="Uscita" fill="#3b82f6" stackId="a" radius={[0, 0, 0, 0]} />
            <Bar dataKey="missed" name="Perse" fill="#ef4444" stackId="a" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (metric === 'missed_rate' || metric === 'answered_rate') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={metric === 'missed_rate' ? "#ef4444" : "#10b981"} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={metric === 'missed_rate' ? "#ef4444" : "#10b981"} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickFormatter={formatDateTick}
            />
            <YAxis unit="%" tick={{fontSize: 12}} />
            <Tooltip 
              labelFormatter={formatDateTick}
              formatter={(value) => `${parseFloat(value).toFixed(1)}%`} 
              contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            />
            <Legend wrapperStyle={{ paddingTop: 20 }} />
            <Area 
              type="monotone" 
              dataKey="value" 
              name={getChartTitle()} 
              stroke={metric === 'missed_rate' ? "#ef4444" : "#10b981"} 
              fillOpacity={1} 
              fill="url(#colorValue)" 
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            tickFormatter={formatDateTick}
          />
          <YAxis tick={{fontSize: 12}} />
          <Tooltip 
            labelFormatter={formatDateTick}
            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
          />
          <Legend wrapperStyle={{ paddingTop: 20 }} />
          <Line 
            type="monotone" 
            dataKey="value" 
            name={getChartTitle()} 
            stroke="#8b5cf6" 
            strokeWidth={3} 
            dot={{ r: 4, fill: "#8b5cf6", strokeWidth: 2, stroke: "#fff" }} 
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <BarChartLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Andamento Chiamate CloudTalk
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Indicatori e analisi delle performance delle chiamate
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon size={18} />}>
          Esporta
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
                <FilterIcon size={20} className="text-gray-500" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 2 }}>
                  Filtri:
                </Typography>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Periodo</InputLabel>
                  <Select
                    value={period}
                    label="Periodo"
                    onChange={(e) => setPeriod(e.target.value)}
                  >
                    <MenuItem value="today">Giorno corrente</MenuItem>
                    <MenuItem value="this_week">Settimana corrente</MenuItem>
                    <MenuItem value="this_month">Mese corrente</MenuItem>
                    <MenuItem value="last_3_months">Ultimi 3 mesi</MenuItem>
                    <MenuItem value="last_6_months">Ultimi 6 mesi</MenuItem>
                    <MenuItem value="last_12_months">Ultimi 12 mesi</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Visualizzazione</InputLabel>
                  <Select
                    value={metric}
                    label="Visualizzazione"
                    onChange={(e) => setMetric(e.target.value)}
                  >
                    <MenuItem value="calls_count">Numero chiamate</MenuItem>
                    <MenuItem value="total_duration">Durata totale (minuti)</MenuItem>
                    <MenuItem value="avg_duration">Durata media</MenuItem>
                    <MenuItem value="missed_rate">Tasso chiamate perse (%)</MenuItem>
                    <MenuItem value="answered_rate">Tasso risposta (%)</MenuItem>
                    <MenuItem value="wait_time">Tempo medio attesa</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Raggruppa per</InputLabel>
                  <Select
                    value={groupBy}
                    label="Raggruppa per"
                    onChange={(e) => setGroupBy(e.target.value)}
                  >
                    <MenuItem value="day">Giorno</MenuItem>
                    <MenuItem value="week">Settimana</MenuItem>
                    <MenuItem value="month">Mese</MenuItem>
                    <MenuItem value="hour">Ora del giorno</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Direzione</InputLabel>
                  <Select
                    value={filters.direction}
                    label="Direzione"
                    onChange={(e) => setFilters({...filters, direction: e.target.value})}
                  >
                    <MenuItem value="">Tutte</MenuItem>
                    <MenuItem value="inbound">Entrata</MenuItem>
                    <MenuItem value="outbound">Uscita</MenuItem>
                  </Select>
                </FormControl>

                <Autocomplete
                  size="small"
                  sx={{ minWidth: 240 }}
                  options={agentSelectOptions}
                  value={selectedAgent}
                  onChange={(event, newValue) =>
                    setFilters({ ...filters, agent_id: newValue?.value || '' })
                  }
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField {...params} label="Agente" placeholder="Tutti" />
                  )}
                />
              </Box>

              <Typography variant="h6" gutterBottom>
                {getChartTitle()} - {groupBy === 'day' ? 'Giornaliero' : (groupBy === 'month' ? 'Mensile' : groupBy)}
              </Typography>
              
              <ChartWrapper>
                {renderChart()}
              </ChartWrapper>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CloudTalkKPI;
